import type { FC } from 'react';
import { useEffect } from 'react';
import { Menu } from '@/shell/PageFrame/Menu';
import { MenuItem } from '@/shell/PageFrame/MenuItem';
import { PageFrame as Component } from '@/shell/PageFrame/PageFrame';
import {
  PROGRAM_BASE_PATH,
  VEGETATION_INTELLIGENCE_PATH,
} from '@/app/constants';
import { useTablet, useSmallScreen } from '@/hooks/useWindowDimension';
import { useMobileInspectMode } from '@/hooks/useMobileInspectMode';
import { useAppDispatch } from '@/app/hooks';
import { updateInspectMode } from '@/features/analysisMapSlice';
import { Calendar, type Icon, Map, TrendingUp } from 'react-feather';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { withPreservedSearch } from '@/hooks/useNavigateWithSearch';
import { useLocation, type To } from 'react-router-dom';
import { useSyncClient } from '@/hooks/useSyncClient';
import { useAppFlags } from '@/hooks/useAppFlags';
import { useLDIdentified } from '@/hooks/useLDIdentified';
import { useClientTerminology } from '@/hooks/useClientTerminology';

interface Props {
  selected?: SupportedPage;
  contentOverflow?: string;
  children?: React.ReactNode;
}

type SupportedPage =
  | 'Map'
  | 'Dashboard'
  | 'Schedule'
  | 'Programs'
  | 'VegetationIntelligence';

export const PageLayout: FC<Props> = ({
  contentOverflow = 'auto',
  selected,
  ...props
}) => {
  const location = useLocation();
  const isSmallScreen = useSmallScreen();
  const dispatch = useAppDispatch();
  const tabletDimensions = useTablet();
  const { flagAeptxVegetationIntelligenceDashboard } = useAppFlags();
  const isLDIdentified = useLDIdentified();
  const { getClientTerm } = useClientTerminology();

  // This will automatically determine the title from the routes.ts file
  useDocumentTitle();
  useSyncClient();

  const menu = (
    <Menu>
      {createMenuItem(
        Map,
        'Map',
        selected,
        withPreservedSearch('/', location.search, { sessionOnly: true })
      )}
      {createMenuItem(
        TrendingUp,
        'Dashboard',
        selected,
        withPreservedSearch('/dashboard', location.search, {
          sessionOnly: true,
        })
      )}
      {/* Don't preserve release param when navigating to programs, as we want to show the latest release by default */}
      {createMenuItem(Calendar, 'Programs', selected, PROGRAM_BASE_PATH)}
      {isLDIdentified && flagAeptxVegetationIntelligenceDashboard && (
        <MenuItem
          name="Detected"
          label={getClientTerm('vegetationIntelligence.navLabel')}
          selected={selected === 'VegetationIntelligence'}
          to={VEGETATION_INTELLIGENCE_PATH}
        />
      )}
    </Menu>
  );

  const isMobileInspectMode = useMobileInspectMode();

  useEffect(() => {
    if (isSmallScreen || tabletDimensions) {
      dispatch(updateInspectMode(false));
    }
  }, [tabletDimensions, dispatch, isSmallScreen]);

  return (
    <Component
      menu={isMobileInspectMode ? undefined : menu}
      contentOverflow={contentOverflow}
      inspectMode={isMobileInspectMode}
    >
      {props.children}
    </Component>
  );
};

const createMenuItem = (
  icon: Icon,
  name: SupportedPage,
  selected: SupportedPage | undefined,
  to: To
) => (
  <MenuItem
    Component={icon}
    label={name}
    selected={selected === name}
    to={to}
  />
);
